const updateHeroFirstVideoSlide = () => {
  const splideSlider = !document.querySelectorAll(".avvio-hero .splide").length
    ? document.querySelectorAll(".avvio-hero-mobile .splide")
    : document.querySelectorAll(".avvio-hero .splide");

  splideSlider.forEach((slider) => {
    const isInitialized = slider.classList.contains("is-initialized");
    if (isInitialized) return false;

    // Reset first slide class
    const firstSlide = slider.querySelector(".splide__slide");
    const heroFirstSlideVideo = firstSlide?.querySelector("video");

    if (heroFirstSlideVideo) {
      heroFirstSlideVideo.classList.remove("js-avvio-hls-video");
      heroFirstSlideVideo.classList.add("js-avvio-hls-video-first-slide");
    }
  });
};

// Update the Hero Slider First slide Video Class to initialize video with the Splide slider
updateHeroFirstVideoSlide();

class VideoJs {
  constructor(options) {
    this.defaultOptions = videojs.obj.merge(
      {
        selector: ".js-avvio-hls-video",
        autoplayClass: "js-avvio-hls-video-autoplay",
        preload: "auto",
        playsinline: true,
        loadingSpinner: false,
        fluid: false,
        videoControls: {
          controlPanelSelector: ".js-avvio-video__controls",
          playButtonSelector: ".js-avvio-video__play-button",
          pauseButtonSelector: ".js-avvio-video__pause-button",
          muteButtonSelector: ".js-avvio-video__mute-button",
          muteIconSelector:
            ".js-avvio-video__mute-button .avvio-video__control-icon",
        },

        // videojs-http-streaming (VHS) support, add support for HLS video not natively supported devices.
        html5: {
          vhs: {
            overrideNative: true,
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false,
        },
      },
      options
    );

    // new options object for current instance
    this.options = videojs.obj.merge({}, this.defaultOptions);
  }

  // Autoplay
  handleAutoplay(video) {
    if (!video) return false;

    const { autoplayClass } = this.options;

    const hasAutoplay = video.classList.contains(autoplayClass);
    this.options.autoplay = hasAutoplay;
  }

  // Play video only in viewport
  handleViewport(player) {
    // REF: https://videojs.com/blog/autoplay-best-practices-with-video-js/
    if (!player.play) return false;

    const handlePlayback = (player) => {
      // handle playback in the viewport
      let observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Prevent video Play or Pause if it's inside slider and slide is not in view -- splide "fade" type
            const isSlider = player.el().closest(".splide");
            const isSlideVisible = player
              .el()
              .closest(".splide__slide")
              ?.classList.contains("is-visible");
            if (isSlider && !isSlideVisible) {
              player.pause();
              return;
            }

            // PLAY the video at more than or equal to 80% visibility
            if (entry.intersectionRatio >= 0.8 && player.paused()) {
              player.play();
            }
            // PAUSE the video at less than or equal to 40% visibility
            else if (entry.intersectionRatio <= 0.4 && !player.paused()) {
              player.pause();
            }
          });
        },
        { threshold: [0.4, 0.8] } // Update the threshold 1 is when the video is 100% in the viewport
      );
      observer.observe(player.el());
    };

    if (player.getAttribute("autoplay")) {
      let isHandlePlayback = false;
      // We can only control playback without interaction if video is mute
      // video.muted = true;
      // Play is a promise so we need to check we have it
      let playPromise = player.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          handlePlayback(player);
          isHandlePlayback = true;
        });
      }

      // Fix for the firefox bug where video play promise not resolved
      // It will observe even video is in paused state
      if (player.paused() && !isHandlePlayback) {
        handlePlayback(player);
        isHandlePlayback = true;
      }
    }
  }

  sliderAutoplayToggle(event, playState) {
    const slider = event.target.closest(".splide");
    if (!slider) return false;

    const video = event.target.querySelector("video");

    // Prevent to start slider autoplay - before first video slide ended or before user interaction
    if (
      slider.hasFirstSlideVideo &&
      slider.isFirstSlideVideoEnded === undefined
    )
      return false;

    // Prevent to handle slider autoplay - check if video is ended and video has not loop enabled
    if (video.ended && !video.loop) return false;

    // Prevent to handle slider autoplay - If the slide is not visible or the video of the previous slide is paused
    const isCarouselSlideVisible = event.target
      .closest(".splide__slide")
      ?.classList.contains("is-visible");
    if (!isCarouselSlideVisible) return false;

    const { sliderAutoplay } = slider;
    if (!sliderAutoplay) return false;

    playState ? sliderAutoplay.play() : sliderAutoplay.pause();

    return true;
  }

  handleVideoControls(player) {
    if (!player) return false;

    const { controlPanelSelector } = this.options.videoControls;
    const controlPanelClass = controlPanelSelector.slice(1);

    if (!player.el()?.nextElementSibling?.classList.contains(controlPanelClass))
      return false;

    const controlPanel = player.el().nextElementSibling;
    const blockName = controlPanel.dataset.videoControlsBlockName;

    controlPanel.style.display = "block";

    const togglePlayClass = () => {
      if (!player.paused()) {
        controlPanel.classList.contains(`${blockName}__controls--paused`) &&
          controlPanel.classList.remove(`${blockName}__controls--paused`);
        controlPanel.classList.add(`${blockName}__controls--playing`);
      } else {
        controlPanel.classList.contains(`${blockName}__controls--playing`) &&
          controlPanel.classList.remove(`${blockName}__controls--playing`);
        controlPanel.classList.add(`${blockName}__controls--paused`);
      }
    };
    togglePlayClass();

    player.on("play", (event) => {
      togglePlayClass();
    });

    player.on("pause", (event) => {
      togglePlayClass();
    });

    const { playButtonSelector, pauseButtonSelector } =
      this.options.videoControls;

    const playButton = controlPanel.querySelector(playButtonSelector);
    const pauseButton = controlPanel.querySelector(pauseButtonSelector);
    playButton?.addEventListener("click", () => {
      player.controlBar.playToggle.el().click();
    });
    pauseButton?.addEventListener("click", () => {
      player.controlBar.playToggle.el().click();
    });

    const { muteButtonSelector, muteIconSelector } = this.options.videoControls;
    const muteButton = controlPanel.querySelector(muteButtonSelector);
    const muteIcon = controlPanel.querySelector(muteIconSelector);

    const toggleMuteClass = () => {
      if (!muteIcon) return false;
      if (player.muted()) {
        muteIcon.classList.contains("avvio-icon-mute") &&
          muteIcon.classList.remove("avvio-icon-mute");
        muteIcon.classList.add("avvio-icon-unmute");

        controlPanel.classList.contains(`${blockName}__controls--unmuted`) &&
          controlPanel.classList.remove(`${blockName}__controls--unmuted`);
        controlPanel.classList.add(`${blockName}__controls--muted`);
      } else {
        muteIcon.classList.contains("avvio-icon-unmute") &&
          muteIcon.classList.remove("avvio-icon-unmute");
        muteIcon.classList.add("avvio-icon-mute");

        controlPanel.classList.contains(`${blockName}__controls--muted`) &&
          controlPanel.classList.remove(`${blockName}__controls--muted`);
        controlPanel.classList.add(`${blockName}__controls--unmuted`);
      }
    };
    toggleMuteClass();

    player.on("volumechange", (event) => {
      toggleMuteClass();
    });

    muteButton?.addEventListener("click", () => {
      player.controlBar.volumePanel.muteToggle.el().click();
    });
  }

  initInstance(video, events) {
    if (!video) return false;

    // Set video duration to the current slide
    const slide = video.closest(".splide__slide");

    // Update options before Video js init
    this.handleAutoplay(video);

    // Init video js for current instance
    const player = videojs(video, this.options);

    player.on("loadstart", () => {
      this.handleViewport(player);
      this.handleVideoControls(player);
    });

    // loadedmetadata event listener
    player.on("loadedmetadata", function () {
      const duration = video.duration || player.duration();
      slide &&
        duration &&
        slide.setAttribute("data-splide-interval", parseFloat(duration) * 1000);
    });

    // Handle slider autoplay
    player.on("play", (event) => {
      this.sliderAutoplayToggle(event, true);
    });

    player.on("pause", (event) => {
      this.sliderAutoplayToggle(event, false);
    });

    // Execute the events form externally
    events &&
      events.length &&
      events.forEach((event) => {
        if (!event.eventType || !event.cb) return false;

        if (event.eventType === "onended") {
          player.on("timeupdate", () => {
            if (player.currentTime() <= 0.5) return;

            // console.log(player.currentTime() + 0.5 + " >= " + player.duration());

            if (player.currentTime() + 0.5 >= player.duration()) {
              event.cb();
            }
          });

          return false;
        }

        player.on(event.eventType, event.cb);
      });

    // Reset options
    this.options = videojs.obj.merge({}, this.defaultOptions);
  }

  init(events) {
    const { selector } = this.options;
    const videos = document.querySelectorAll(selector);

    if (!videos.length) return false;

    videos.forEach((video) => {
      this.initInstance(video, events);
    });
  }
}

const videoJs = new VideoJs();

/**
 * Initialize the script when the block is previewed within the editor
 * https://www.advancedcustomfields.com/resources/javascript-api/
 */
if (window.acf) {
  window.acf.addAction("render_block_preview/type=avvio-hero", () =>
    videoJs.init()
  );
  window.acf.addAction("render_block_preview/type=avvio-image-video", () =>
    videoJs.init()
  );
  window.acf.addAction("render_block_preview/type=avvio-fifty-fifty", () =>
    videoJs.init()
  );
} else {
  window.addEventListener("load", () => {
    videoJs.init();
  });
}
